import type {
  PaymentMethodToken,
  TokenizeCardPaymentMethodInput,
} from "./generated/graphql";

import type {
  GraphQLExtensions,
  AllowedFrameStyle,
  ClientToken,
  GenericError,
  HighnoteEnvironment,
  InvalidCredentialError,
  PaymentCardId,
} from ".";

export type { PaymentMethodToken };

export const secureInputFields = [
  "pin",
  "cardNumber",
  "expirationMonth",
  "expirationYear",
  "expirationDate",
  "securityCode",
] as const;

export type SecureInputsFields = (typeof secureInputFields)[number];

export const cardBrands = ["visa", "mastercard", "amex", "discover"] as const;

export type SecureInputsCardBrand = (typeof cardBrands)[number];

export interface SecureInputsPinElementConfig {
  // A valid [DOMString](https://developer.mozilla.org/en-US/docs/Web/API/DOMString) that is a valid CSS Selector.
  //
  // Examples: "#foo", ".bar", '[data-foo="bar"]'
  //
  selector: string;

  // @deprecated Supply `clientToken` in SecureInputsConfig instead.
  clientToken?: ClientToken;
  paymentCardId: PaymentCardId;
  styles?: AllowedFrameStyle;

  /**
   * Mask PIN digits (like a password field). Default: `false`.
   */
  masked?: boolean;

  /**
   * Render a visibility toggle button inside the iframe to reveal/hide the PIN.
   * Only takes effect when `masked` is `true`. Default: `false`.
   */
  showToggle?: boolean;
}

export interface SecureInputsCardNumberElementConfig {
  // A valid [DOMString](https://developer.mozilla.org/en-US/docs/Web/API/DOMString) that is a valid CSS Selector.
  //
  // Examples: "#foo", ".bar", '[data-foo="bar"]'
  //
  selector: string;
  styles?: AllowedFrameStyle;
}

export interface SecureInputsCardExpirationMonthElementConfig {
  // A valid [DOMString](https://developer.mozilla.org/en-US/docs/Web/API/DOMString) that is a valid CSS Selector.
  //
  // Examples: "#foo", ".bar", '[data-foo="bar"]'
  //
  selector: string;
  styles?: AllowedFrameStyle;
}

export interface SecureInputsCardExpirationYearElementConfig {
  // A valid [DOMString](https://developer.mozilla.org/en-US/docs/Web/API/DOMString) that is a valid CSS Selector.
  //
  // Examples: "#foo", ".bar", '[data-foo="bar"]'
  //
  selector: string;
  styles?: AllowedFrameStyle;
}

export interface SecureInputsCardExpirationDateElementConfig {
  // A valid [DOMString](https://developer.mozilla.org/en-US/docs/Web/API/DOMString) that is a valid CSS Selector.
  //
  // Examples: "#foo", ".bar", '[data-foo="bar"]'
  //
  selector: string;
  styles?: AllowedFrameStyle;
}

export interface SecureInputsCardSecurityCodeElementConfig {
  // A valid [DOMString](https://developer.mozilla.org/en-US/docs/Web/API/DOMString) that is a valid CSS Selector.
  //
  // Examples: "#foo", ".bar", '[data-foo="bar"]'
  //
  selector: string;
  styles?: AllowedFrameStyle;
}

export type SecureInputsElementConfig =
  | SecureInputsCardExpirationDateElementConfig
  | SecureInputsCardExpirationMonthElementConfig
  | SecureInputsCardExpirationYearElementConfig
  | SecureInputsCardNumberElementConfig
  | SecureInputsCardSecurityCodeElementConfig
  | SecureInputsPinElementConfig;

export enum SecureInputsGlobalPostMessageEventKind {
  GENERIC_ERROR = "GENERIC_ERROR",
  INVALID_CREDENTIAL_ERROR = "INVALID_CREDENTIAL_ERROR",
  USER_INPUT_ERROR = "USER_INPUT_ERROR",
  REQUEST_ERROR = "REQUEST_ERROR",
  ON_INPUT = "ON_INPUT",
  ON_SUBMIT = "ON_SUBMIT",
  CHANGE_CARD_PIN = "CHANGE_CARD_PIN",
  TOKENIZE_CARD = "TOKENIZE_CARD",
  ON_CARD_INPUT = "ON_CARD_INPUT",
  ON_PIN_CHANGE_SUCCESS = "ON_PIN_CHANGE_SUCCESS",
  ON_TOKENIZE_CARD_SUCCESS = "ON_TOKENIZE_CARD_SUCCESS",
  ON_STATE_CHANGE = "ON_STATE_CHANGE",
  ON_READY = "ON_READY",
  VALIDATION_ERROR = "VALIDATION_ERROR",
}

export interface SecureInputsGlobalGraphQLPostMessageEvent {
  __typename: SecureInputsGlobalPostMessageEventKind.INVALID_CREDENTIAL_ERROR;
  context: GraphQLExtensions;
}

export type SecureInputsAdditionalTokenizationData = Omit<
  TokenizeCardPaymentMethodInput,
  "paymentCard"
>;

export type SecureInputsValidityState = Pick<
  ValidityState,
  | "badInput"
  | "customError"
  | "rangeOverflow"
  | "rangeUnderflow"
  | "stepMismatch"
  | "tooLong"
  | "tooShort"
  | "valid"
  | "valueMissing"
>;

export type SecureInputsElementState = Readonly<{
  /**
   * validity state of the field.
   */
  validityState: SecureInputsValidityState;

  /**
   * If customError is true, this will contain the custom error message.
   */
  customErrorMessage?: string;
}>;

export interface SecureInputsPostMessageEventOnReady {
  __typename: SecureInputsGlobalPostMessageEventKind.ON_READY;
}

export interface SecureInputsPostMessageEventOnStateChange {
  __typename: SecureInputsGlobalPostMessageEventKind.ON_STATE_CHANGE;
  field: SecureInputsFields;
  state: SecureInputsElementState;
}

export interface SecureInputsPostMessageTokenizeCard {
  __typename: SecureInputsGlobalPostMessageEventKind.TOKENIZE_CARD;
  additionalData?: SecureInputsAdditionalTokenizationData;
}

export interface SecureInputsPostMessageOnTokenizeCardSuccess {
  __typename: SecureInputsGlobalPostMessageEventKind.ON_TOKENIZE_CARD_SUCCESS;

  paymentMethodToken: PaymentMethodToken;
}

// Posted by the card frame when `submit()` finds a field that fails a check
// (invalidFields) or a field whose frame cannot be read (unavailableFields).
// No request is sent.
export interface SecureInputsPostMessageValidationError {
  __typename: SecureInputsGlobalPostMessageEventKind.VALIDATION_ERROR;
  invalidFields: SecureInputsFields[];
  unavailableFields: SecureInputsFields[];
  fields: SecureInputsFields[];
  message: string;
}

export type SecureInputsPostMessageEvent =
  | SecureInputsPostMessageEventOnReady
  | SecureInputsPostMessageEventOnStateChange
  | SecureInputsPostMessageOnTokenizeCardSuccess
  | SecureInputsPostMessageTokenizeCard
  | SecureInputsPostMessageValidationError
  | {
      __typename: SecureInputsGlobalPostMessageEventKind.CHANGE_CARD_PIN;
    }
  | {
      __typename: SecureInputsGlobalPostMessageEventKind.GENERIC_ERROR;
      message: string;
    }
  | {
      __typename: SecureInputsGlobalPostMessageEventKind.ON_CARD_INPUT;
      field: SecureInputsFields;
      state: {
        value: string;
      };
    }
  | {
      __typename: SecureInputsGlobalPostMessageEventKind.ON_INPUT;
      field: SecureInputsFields;
      config: SecureInputsElementConfig;
    }
  | {
      __typename: SecureInputsGlobalPostMessageEventKind.ON_PIN_CHANGE_SUCCESS;
      field: SecureInputsFields;
    }
  | {
      __typename: SecureInputsGlobalPostMessageEventKind.ON_SUBMIT;
    }
  | {
      __typename: SecureInputsGlobalPostMessageEventKind.REQUEST_ERROR;
      field: SecureInputsFields;
      message: string;
      context: GraphQLExtensions;
    }
  | {
      __typename: SecureInputsGlobalPostMessageEventKind.USER_INPUT_ERROR;
      field: SecureInputsFields;
      message: string;
    };

// Construct for postMessage events meant to be sent across origins.
export type SecureInputsGlobalPostMessageEvent =
  | SecureInputsGlobalGraphQLPostMessageEvent
  | SecureInputsPostMessageEvent;

export type SecureInputsFieldInputError = Readonly<{
  name: "SecureInputsFieldInputError";
  field: SecureInputsFields;
  message: string;
}>;

export type SecureInputsRequestError = Readonly<{
  name: "SecureInputsRequestError";
  field: string;
  message?: string;
  context: GraphQLExtensions;
}>;

// An error that occurs when the secure channel delivering credentials to a
// secure inputs frame cannot be established.
export type SecureInputsHandshakeError = Readonly<{
  name: "SecureInputsHandshakeError";
  message: string;
}>;

/**
 * `submit()` found a field that fails a check or a field it could not read.
 * No tokenization request was sent.
 *
 * `invalidFields` fail a format check (digits-only card number of a brand
 * length and not an excluded brand; expiry format; security code length for
 * the card's brand), or were reported missing or empty by the API.
 * `unavailableFields` could not be read when `submit()` ran (for example a
 * field still loading). `fields` lists both. `message` names each field and
 * why.
 */
export type SecureInputsValidationError = Readonly<{
  name: "SecureInputsValidationError";
  message: string;
  invalidFields: SecureInputsFields[];
  unavailableFields: SecureInputsFields[];
  fields: SecureInputsFields[];
}>;

// A generic catch-all error. Typically, this captures exceptions that are not recoverable.
export type SecureInputsError = Readonly<
  | GenericError
  | InvalidCredentialError
  | SecureInputsFieldInputError
  | SecureInputsHandshakeError
  | SecureInputsRequestError
  | SecureInputsValidationError
>;

export type SecureInputsTokenizationError = Readonly<
  | GenericError
  | InvalidCredentialError
  | SecureInputsHandshakeError
  | SecureInputsRequestError
  | SecureInputsValidationError
>;

export type SecureInputsConfigError = Readonly<SecureInputsError>;

export type SecureInputsInputCallback = (
  field: SecureInputsFields,
  config: SecureInputsElementConfig
) => void;

export type SecureInputsErrorCallback = (error: SecureInputsError) => void;

export type SecureInputsSuccessCallback = (field: SecureInputsFields) => void;

export type SecureInputsTokenizationErrorCallback = (
  error: SecureInputsTokenizationError
) => void;

export type SecureInputsTokenizationSuccessCallback = (
  paymentMethodToken: PaymentMethodToken
) => void;

export type SecureInputsOnReadyCallback = () => void;

export type SecureInputsOnStateChangeCallback = (
  field: SecureInputsFields,
  state: SecureInputsElementState
) => void;

// An error that occurs when unable to submit fields
export type SecureInputsSubmitError = Readonly<{
  name: "SecureInputsSubmitError";
  message?: string;
}>;

export interface SecureInputsElements {
  cardNumber?: SecureInputsCardNumberElementConfig;
  expirationMonth?: SecureInputsCardExpirationMonthElementConfig;
  expirationYear?: SecureInputsCardExpirationYearElementConfig;
  expirationDate?: SecureInputsCardExpirationDateElementConfig;
  securityCode?: SecureInputsCardSecurityCodeElementConfig;
  pin?: SecureInputsPinElementConfig;
}

export type SecureInputsElementsForTokenization =
  | {
      cardNumber: SecureInputsCardNumberElementConfig;
      expirationDate: SecureInputsCardExpirationDateElementConfig;
      securityCode: SecureInputsCardSecurityCodeElementConfig;
    }
  | {
      cardNumber: SecureInputsCardNumberElementConfig;
      expirationMonth: SecureInputsCardExpirationMonthElementConfig;
      expirationYear: SecureInputsCardExpirationYearElementConfig;
      securityCode: SecureInputsCardSecurityCodeElementConfig;
    };

export interface SecureInputsElementsForCardNumberOnly {
  cardNumber: SecureInputsCardNumberElementConfig;
}

export interface SecureInputsElementsForPinEntry {
  pin: SecureInputsPinElementConfig;
}

export interface SecureInputsTokenizationConfig {
  clientToken: ClientToken;
  elements:
    | SecureInputsElementsForCardNumberOnly
    | SecureInputsElementsForTokenization;
  onError: SecureInputsTokenizationErrorCallback;
  /**
   * A callback that is invoked when the card is successfully tokenized.
   * Only required for the tokenization flow.
   */
  onSuccess: SecureInputsTokenizationSuccessCallback;

  /** The environment to use for making requests to the Highnote API.
   *
   * default value: 'test'
   */
  environment?: HighnoteEnvironment;

  /**
   * A callback that is invoked when a secure inputs element state has changed, like when the input is valid/invalid.
   * Can be used to show/hide validation messages to the user or to enable/disable a submit button.
   */
  onStateChange?: SecureInputsOnStateChangeCallback;

  /**
   * A callback that is invoked when all secure input elementss are initialized ready to be interacted with.
   *
   * This is useful for hiding a loading spinner or showing the secure input elements to the user.
   */
  onReady: SecureInputsOnReadyCallback;
  /**
   * Specifies a list of card brands to be excluded from the tokenization process,
   * preventing the card brands from being processed.
   */
  excludedCardBrands?: SecureInputsCardBrand[];
}

export interface SecureInputsConfig {
  clientToken?: ClientToken;
  elements: SecureInputsElementsForPinEntry;
  onInput: SecureInputsInputCallback;
  onError: SecureInputsErrorCallback;
  onSuccess: SecureInputsSuccessCallback;

  /** The environment to use for making requests to the Highnote API.
   *
   * default value: 'test'
   */
  environment?: HighnoteEnvironment;
}

export interface SecureInputsTokenizationSubmitArguments {
  additionalData?: SecureInputsAdditionalTokenizationData;
}

export interface SecureInputsRenderedFields {
  // Submits all input fields.
  submit: () => void;
  // Unmount all of the embedded iframes.
  unmount: () => Promise<void>;
}

export interface SecureInputsTokenizationRenderedFields {
  // Submits all input fields.
  submit: (args: SecureInputsTokenizationSubmitArguments) => void;
  // Unmount all of the embedded iframes.
  unmount: () => Promise<void>;
}
